import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Play, 
  Tv, 
  Music2, 
  Volume2, 
  LayoutGrid, 
  Radio, 
  Smartphone, 
  Mic2, 
  Waves,
  Filter,
  X,
  Eye
} from "lucide-react";
import VideoCard from "./VideoCard";

// Video Categories - similar to the client work types but for brand videos
const VIDEO_CATEGORIES = [
  { id: "all", label: "All Videos", icon: LayoutGrid },
  { id: "brand-story", label: "Brand Story", icon: Tv },
  { id: "product", label: "Product", icon: Eye },
  { id: "campaign", label: "Campaign", icon: Volume2 },
  { id: "testimonial", label: "Testimonial", icon: Mic2 },
  { id: "behind-scenes", label: "Behind the Scenes", icon: Play },
  { id: "commercial", label: "Commercial", icon: Radio },
  { id: "social", label: "Social Media", icon: Smartphone },
  { id: "event", label: "Event", icon: Music2 },
  { id: "tutorial", label: "How-To", icon: Waves },
];

/**
 * DiscoveryVideoGrid Component
 * 
 * Enhanced video grid for discovery sections that allows tab-based filtering
 * and video labeling, similar to the client work grid functionality.
 */
const DiscoveryVideoGrid = ({ 
  videos = [], 
  title = "Brand Videos", 
  showCategories = true, 
  defaultCategory = "all",
  className = "",
  maxColumns = 3,
  enableModal = true
}) => {
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [modalVideo, setModalVideo] = useState(null);

  // Filter videos based on selected category
  const filteredVideos = videos.filter(video => {
    if (selectedCategory === "all") return true;
    
    // Check if video has categories (array) or single category (string)
    const videoCategories = Array.isArray(video.categories) 
      ? video.categories 
      : video.category 
        ? [video.category] 
        : ["all"];
    
    return videoCategories.includes(selectedCategory);
  });

  // Calculate grid columns based on video count and max columns
  const getGridCols = () => {
    const videoCount = filteredVideos.length;
    if (videoCount === 1) return "grid-cols-1";
    if (videoCount === 2) return "grid-cols-1 md:grid-cols-2";
    if (maxColumns >= 3) return "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";
    if (maxColumns === 2) return "grid-cols-1 md:grid-cols-2";
    return "grid-cols-1";
  };

  const CategoryButton = ({ category, isActive, onClick }) => {
    const Icon = category.icon;
    return (
      <motion.button
        onClick={onClick}
        className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
          isActive
            ? "bg-[var(--primary-color)] text-white shadow-lg"
            : "bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10"
        }`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Icon size={16} />
        {category.label}
      </motion.button>
    );
  };

  const VideoModal = ({ video, onClose }) => {
    if (!video) return null;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="relative max-w-4xl w-full bg-black rounded-2xl overflow-hidden border border-white/20"
          onClick={e => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition"
          >
            <X size={20} />
          </button>
          
          <div className="aspect-video">
            <VideoCard
              src={video.url}
              poster={video.poster}
              title={video.title}
            />
          </div>
          
          {(video.title || video.description) && (
            <div className="p-6 bg-black/90">
              {video.title && (
                <h3 className="text-xl font-semibold text-white mb-2">
                  {video.title}
                </h3>
              )}
              {video.description && (
                <p className="text-white/70 leading-relaxed">
                  {video.description}
                </p>
              )}
              {video.categories && video.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {video.categories.map(cat => {
                    const categoryInfo = VIDEO_CATEGORIES.find(c => c.id === cat);
                    return (
                      <span
                        key={cat}
                        className="px-3 py-1 bg-[var(--primary-color)]/20 text-[var(--primary-color)] rounded-full text-sm"
                      >
                        {categoryInfo?.label || cat}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    );
  };

  if (!videos || videos.length === 0) {
    return (
      <div className={`space-y-6 ${className}`}>
        {title && (
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
            {title}
          </h3>
        )}
        <div className="text-center py-12 bg-white/5 rounded-lg border border-white/10">
          <div className="w-16 h-16 mx-auto mb-4 bg-white/10 rounded-full flex items-center justify-center">
            <Tv size={24} className="text-white/50" />
          </div>
          <h4 className="text-lg font-medium text-white mb-2">No Videos Available</h4>
          <p className="text-white/60 max-w-md mx-auto">
            Brand videos will appear here once they're added to the profile.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {title && (
        <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
          {title}
        </h3>
      )}

      {/* Category Filter Tabs */}
      {showCategories && VIDEO_CATEGORIES.length > 1 && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-white/70">
            <Filter size={16} />
            <span className="text-sm font-medium">Filter by category:</span>
          </div>
          <div className="flex flex-wrap gap-3">
            {VIDEO_CATEGORIES.map(category => (
              <CategoryButton
                key={category.id}
                category={category}
                isActive={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Videos Grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className={`grid gap-6 ${getGridCols()}`}
        >
          {filteredVideos.map((video, index) => (
            <motion.div
              key={`${video.id || index}-${selectedCategory}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              className="group cursor-pointer"
              onClick={() => enableModal && setModalVideo(video)}
            >
              <div className="relative aspect-video rounded-lg overflow-hidden bg-black/60 border border-white/10 shadow-lg group-hover:shadow-xl transition-all duration-300">
                <VideoCard
                  src={video.url}
                  poster={video.poster}
                  title={video.title}
                />
                
                {/* Video overlay info */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 right-4">
                    {video.title && (
                      <h4 className="text-white font-semibold text-lg mb-1 line-clamp-2">
                        {video.title}
                      </h4>
                    )}
                    {video.description && (
                      <p className="text-white/80 text-sm line-clamp-2">
                        {video.description}
                      </p>
                    )}
                    {video.categories && video.categories.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {video.categories.slice(0, 2).map(cat => {
                          const categoryInfo = VIDEO_CATEGORIES.find(c => c.id === cat);
                          return (
                            <span
                              key={cat}
                              className="px-2 py-1 bg-[var(--primary-color)]/80 text-white rounded text-xs"
                            >
                              {categoryInfo?.label || cat}
                            </span>
                          );
                        })}
                        {video.categories.length > 2 && (
                          <span className="px-2 py-1 bg-white/20 text-white rounded text-xs">
                            +{video.categories.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Play button overlay */}
                {enableModal && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-16 h-16 bg-[var(--primary-color)] rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-200">
                      <Play size={24} className="text-white ml-1" fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>

      {/* No videos found for current filter */}
      {filteredVideos.length === 0 && selectedCategory !== "all" && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-8 bg-white/5 rounded-lg border border-white/10"
        >
          <h4 className="text-lg font-medium text-white mb-2">
            No videos found
          </h4>
          <p className="text-white/60 mb-4">
            No videos match the "{VIDEO_CATEGORIES.find(c => c.id === selectedCategory)?.label}" category.
          </p>
          <button
            onClick={() => setSelectedCategory("all")}
            className="px-4 py-2 bg-[var(--primary-color)] text-white rounded-lg font-medium hover:bg-[var(--primary-color)]/90 transition"
          >
            Show All Videos
          </button>
        </motion.div>
      )}

      {/* Video Modal */}
      <AnimatePresence>
        {enableModal && modalVideo && (
          <VideoModal
            video={modalVideo}
            onClose={() => setModalVideo(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DiscoveryVideoGrid;